import React, { useContext, useMemo, useCallback } from "react";
import { CloseOutlined } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";
import { useFavorites } from "../context/FavoritesContext";
import { ThemeContext } from "../context/ThemeContext";
import menuConfig from "../menuConfig";
import "./HeaderFavoriteTabs.css";

// menuConfig'den key'e göre label bulma fonksiyonu - module level'da tanımlandı (her render'da yeniden oluşturulmaz)
const findLabelByKey = (config, key) => {
    for (const item of config) {
        if (item.key === key) {
            return item.label;
        }
        if (item.children) {
            const found = findLabelByKey(item.children, key);
            if (found) return found;
        }
    }
    return null;
};

// Label cache - recursive search'ü önler
const labelCache = new Map();

const HeaderFavoriteTabs = ({ onTabClick, selectedKey }) => {
    const { favorites, removeFavorite } = useFavorites();
    const { themeTokens, isDark } = useContext(ThemeContext);
    const containerClass = `chrome-tabs-container ${isDark ? 'chrome-tabs-dark' : 'chrome-tabs-light'}`;

    // Cache'li label lookup fonksiyonu
    const getCachedLabel = useCallback((key, fallbackLabel) => {
        if (labelCache.has(key)) {
            return labelCache.get(key);
        }
        const label = findLabelByKey(menuConfig, key) || fallbackLabel;
        labelCache.set(key, label);
        return label;
    }, []);

    if (favorites.length === 0) {
        return null;
    }

    return (
        <div className={containerClass}>
            {favorites.map((favorite) => {
                const IconComponent = Icons[favorite.menuIcon];
                const isActive = selectedKey === favorite.menuKey;
                // Cache'li label lookup kullan
                const currentLabel = getCachedLabel(favorite.menuKey, favorite.menuLabel);

                return (
                    <div
                        key={favorite.menuKey}
                        className={`chrome-tab ${isActive ? 'active' : ''}`}
                        onClick={() => onTabClick(favorite.menuKey)}
                        style={{
                            // Inactive pill styles
                            '--pill-bg': isDark
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)',
                            '--pill-bg-hover': isDark
                                ? 'rgba(255, 255, 255, 0.12)'
                                : 'rgba(0, 0, 0, 0.08)',
                            '--pill-border': isDark
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.06)',
                            '--tab-color': isDark
                                ? 'rgba(255, 255, 255, 0.85)'
                                : 'rgba(0, 0, 0, 0.85)',

                            // Active pill styles
                            '--pill-active-bg': `linear-gradient(135deg, ${themeTokens.colorPrimary} 0%, ${themeTokens.colorPrimaryHover || themeTokens.colorPrimary} 100%)`,
                            '--pill-active-color': '#ffffff',
                            '--pill-active-border': themeTokens.colorPrimary,
                            '--pill-active-shadow': `0 4px 12px ${themeTokens.colorPrimary}33`,
                            '--pill-active-shadow-hover': `0 6px 16px ${themeTokens.colorPrimary}44`,

                            // Close button
                            '--pill-close-hover-bg': isActive
                                ? 'rgba(255, 255, 255, 0.2)'
                                : isDark
                                    ? 'rgba(255, 255, 255, 0.15)'
                                    : 'rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {IconComponent && (
                            <IconComponent className="chrome-tab-icon" />
                        )}
                        <span className="chrome-tab-title">
                            {currentLabel}
                        </span>
                        <span
                            className="chrome-tab-close"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeFavorite(favorite.menuKey);
                            }}
                        >
                            <CloseOutlined />
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// React.memo ile sarmalama - gereksiz re-render'ları önler
export default React.memo(HeaderFavoriteTabs);
